<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.previewMode]: previewModeActive
      }
    ]"
  >
    <BaseSpinner v-if="isFetchingAppointment" />
    <div
      v-else
      :class="$style.main"
    >
      <TimeslotPicker
        v-if="showTimeslotPicker"
        @close="showTimeslotPicker = false"
      />
      <div
        ref="scrollContainer"
        :class="$style.content"
      >
        <BaseForm :scrollContainer="$screen !== 's' ? scrollContainer : null">
          <div :class="$style.section">
            <AppointmentParts mb />
          </div>
          <div
            v-if="previewModeActive"
            :class="$style.section"
          >
            <Customer />
            <CustomerMember
              :customerMember="existingAppointmentData.customerMember"
              :mt="0.5"
            />
          </div>
          <!-- Unified date & time: one card with a Custom date / Repeat
               appointment choice (series flag on, creating a new appointment). -->
          <div
            v-if="useUnifiedDate"
            :class="$style.section"
          >
            <BaseCard
              :heading="$t('global.date_time')"
              mb
            >
              <BaseDropdown
                v-if="multiLocation"
                v-model="formData.locationId"
                :options="locationOptions"
                :label="filters.capitalize($t('global.items.location', 1))"
                :mb="0.5"
                v-test="'appointment-location'"
              />

              <!-- Not repeating: the date list, where you can add more dates. -->
              <AppointmentSeriesDates
                v-if="!showRrule"
                @openTimeslots="showTimeslotPicker = true"
              />
              <!-- Repeating: just the start date of the sequence. -->
              <div
                v-else
                :class="$style.dateTimeRow"
              >
                <BaseDateTime
                  v-model="formData.startAt"
                  :label="$t('appointment.recurring.start_date_time')"
                  v-test="'appointment-date'"
                />
                <BaseButton
                  color="inverted"
                  :disabled="!canCheckAvailability"
                  @click="showTimeslotPicker = true"
                  v-test="'appointment-timeslots'"
                >
                  {{ $t('appointment.create.find_timeslots') }}
                </BaseButton>
              </div>

              <div :class="$style.rule" />

              <!-- Repeat is a toggle beside the dates; its controls sit right
                   below it. Turning it on parks any extra dates (kept in the
                   store), turning it off brings them back. -->
              <BaseToggle
                :modelValue="showRrule"
                :label="$t('appointment.recurring.repeat_appointment')"
                :description="$t('appointment.recurring.description')"
                @update:modelValue="toggleRepeat"
                v-test="'appointment-recurring'"
              />
              <div
                v-if="showRrule"
                :class="$style.cardContent"
              >
                <RecurringRules
                  v-model="formData.rrule"
                  :date="formData.startAt"
                  v-test="'appointment-recurring-rules'"
                />
              </div>
            </BaseCard>
          </div>

          <!-- Classic date & time + repeat toggle (editing, or series flag off). -->
          <template v-else>
            <div :class="$style.section">
              <BaseCard
                :heading="$t('global.date_time')"
                mb
              >
                <div :class="$style.dateTimeRow">
                  <BaseDropdown
                    v-if="multiLocation"
                    v-model="formData.locationId"
                    :options="locationOptions"
                    :label="filters.capitalize($t('global.items.location', 1))"
                    v-test="'appointment-location'"
                  />
                  <BaseDateTime
                    v-model="formData.startAt"
                    :label="$t('global.date_time')"
                    v-test="'appointment-date'"
                  />
                  <BaseButton
                    color="inverted"
                    :disabled="
                      !canCheckAvailability ||
                      !!existingAppointmentData.deletedServices.length
                    "
                    @click="showTimeslotPicker = true"
                    v-test="'appointment-timeslots'"
                  >
                    {{ $t('appointment.create.find_timeslots') }}
                  </BaseButton>
                </div>
              </BaseCard>
            </div>

            <div :class="$style.section">
              <BaseCard mb>
                <BaseToggle
                  :modelValue="showRrule"
                  :disabled="disableRrule"
                  :label="$t('appointment.recurring.repeat_appointment')"
                  :description="
                    existingAppointmentData.treatwell
                      ? $t('appointment.recurring_treatwell_disabled')
                      : $t('appointment.recurring.description')
                  "
                  @update:modelValue="toggleRepeat"
                  v-test="'appointment-recurring'"
                />
                <div
                  v-if="showRrule"
                  :class="[$style.cardContent, { [$style.disabled]: disableRrule }]"
                >
                  <RecurringRules
                    v-model="formData.rrule"
                    :date="formData.startAt"
                    v-test="'appointment-recurring-rules'"
                  />
                </div>
                <BaseAlert
                  v-if="disableRrule && !existingAppointmentData.treatwell"
                  color="warning"
                  :text="$t('appointment.recurring_disabled')"
                  mt
                  v-test="'rrule-disabled-message'"
                />
              </BaseCard>
            </div>
          </template>

          <div :class="$style.section">
            <BaseCard
              :heading="filters.capitalize($t('global.items.note', 1))"
              :info="$t('appointment.create.note_info')"
            >
              <div
                v-if="showNoteWarning"
                v-test="'appointment-note-warning'"
              >
                {{ $t('appointment.note_contains_link') }}
                <BaseText
                  inline
                  @click="showNoteWarning = false"
                  v-test="'appointment-note-warning-edit'"
                >
                  {{ $t('global.actions.edit_note') }}
                </BaseText>
              </div>
              <BaseInput
                v-else
                v-model="formData.notes"
                :placeholder="$t('appointment.create.note_placeholder')"
                type="textarea"
                v-test="'appointment-note'"
              />
            </BaseCard>
          </div>
        </BaseForm>
      </div>
      <Sidebar
        :class="$style.sidebar"
        :isSaving="isSaving"
        @submit="onSubmit"
      />
    </div>
  </div>
</template>

<script lang="ts">
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { computed, defineComponent, provide, ref, watchEffect } from 'vue';
import CustomerMember from '@/components/CustomerMember.vue';
import eventBus from '@/event-bus';
import filters from '@/filters';
import { appointmentNoteHasSuspiciousLink } from '@/helpers/appointment-note';
import { modal } from '@/helpers/ui';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useCalendarPreviewStore } from '@/stores/calendar-preview';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useOnboardingStore } from '@/stores/onboarding';
import unleash from '@/unleash';
import {
  type CreateAppointmentsError,
  createAppointment,
  createAppointments,
  updateAppointment,
} from '../actions/appointments';
import RecurringRules from '../RecurringRules.vue';
import AppointmentSeriesDates from './AppointmentSeriesDates.vue';
import AppointmentParts from './appointment-parts/index.vue';
import Customer from './Customer.vue';
import { getPartDuration } from './helpers';
import Sidebar from './Sidebar.vue';
import TimeslotPicker from './TimeslotPicker.vue';

export default defineComponent({
  name: 'CreateEditAppointment',
  components: {
    TimeslotPicker,
    AppointmentParts,
    AppointmentSeriesDates,
    RecurringRules,
    Sidebar,
    Customer,
    CustomerMember,
  },
  setup() {
    const store = useCreateAppointmentStore();
    const {
      formData,
      existingAppointmentData,
      checkAvailability,
      reloadAllAllocations,
    } = store;
    const {
      isFetchingAppointment,
      canCheckAvailability,
      isFormSubmitted,
      availabilityDataChanged,
      sendConfirmationEmail,
    } = storeToRefs(store);
    const { currentOnboardingFlow } = useOnboardingStore();

    const { locations } = useLocationsStore();
    const { multiLocation, isInsideTestBucket } = useCompanyStore();
    const scrollContainer = ref();

    provide('scrollContainer', scrollContainer);

    const totalDuration = computed(() =>
      formData.partsAttributes.reduce(
        (sum, part) => sum + getPartDuration(part),
        0,
      ),
    );

    const disableRrule = computed(
      () =>
        // If the existing appointment already had an rrule, or if it's a Treatwell appointment, the user should not be able to add or edit the rrule
        existingAppointmentData.hasRrule || existingAppointmentData.treatwell,
    );

    const showRrule = ref(false);

    watchEffect(() => {
      if (existingAppointmentData.hasRrule) {
        showRrule.value = true;
      }
    });

    const isSaving = ref(false);

    const { previewModeActive } = storeToRefs(useCalendarPreviewStore());
    const { previewModeEnabled } = useCalendarPreviewStore();
    const { unavailableServices } = storeToRefs(useCreateAppointmentStore());

    const showNoteWarning = ref(false);

    // Bulk appointment creation (behind the AppointmentSeries Unleash flag).
    const series = store.series;
    const seriesFeatureEnabled = unleash.isEnabled('AppointmentSeries');

    // The unified date card (Custom date / Repeat appointment) only applies when
    // creating a new appointment; editing keeps the classic date + repeat cards.
    const useUnifiedDate = computed(
      () => seriesFeatureEnabled && !existingAppointmentData.id,
    );

    const locationOptions = computed(() =>
      locations.map((location) => ({
        value: location.id,
        label: location.internalName,
      })),
    );

    return {
      useUnifiedDate,
      locationOptions,
      formData,
      series,
      seriesFeatureEnabled,
      unavailableServices,
      isSaving,
      existingAppointmentData,
      availabilityDataChanged,
      totalDuration,
      canCheckAvailability,
      sendConfirmationEmail,
      checkAvailability,
      reloadAllAllocations,
      locations,
      isFetchingAppointment,
      isFormSubmitted,
      multiLocation,
      disableRrule,
      showRrule,
      showTimeslotPicker: ref(false),
      v$: useVuelidate(),
      scrollContainer,
      filters,
      previewModeActive,
      previewModeEnabled,
      currentOnboardingFlow,
      isInsideTestBucket,
      showNoteWarning,
    };
  },
  watch: {
    showRrule(value) {
      if (!value) {
        this.formData.rrule = null;
      }
    },
    'formData.notes': {
      handler(notes) {
        if (
          appointmentNoteHasSuspiciousLink(
            notes,
            this.existingAppointmentData.treatwell,
          )
        ) {
          this.showNoteWarning = true;
        }
      },
      once: true,
    },
  },
  methods: {
    onSubmit() {
      this.isFormSubmitted = true;
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      const maxDuration = 959;

      if (this.totalDuration <= 0) {
        modal('warning', {
          message: this.$t('appointment.create.warning_no_duration'),
        });
      } else if (this.totalDuration > maxDuration) {
        modal('warning', {
          message: this.$t('appointment.create.warning_max_duration', {
            max: maxDuration,
          }),
        });
      } else if (this.series.isSeriesMode) {
        this.submitSeries();
      } else if (!this.availabilityDataChanged) {
        this.save();
      } else {
        this.checkAvailability().then((isAvailable) => {
          if (isAvailable) {
            if (this.unavailableServices.length) {
              modal('confirmation', {
                message: this.$t('service.availability.calendar_confirmation'),
              }).then(() => {
                this.save();
              });
            } else {
              this.save();
            }
          } else {
            modal('confirmation', {
              message: this.$t('appointment.not_available'),
            }).then(() => {
              this.save();
            });
          }
        });
      }
    },
    save() {
      this.isSaving = true;

      // The backend crashes when sending input.rrule: null
      // So we need to spread the data into a new object and delete the key if it's null
      // Because why would we take the time to fix the backend..

      const input = {
        ...this.formData,
      };

      if (!input.rrule) {
        input.rrule = undefined;
      }

      const saveAppointment = () => {
        if (this.existingAppointmentData.id) {
          return updateAppointment({
            input,
            original: true,
            previousTime: this.existingAppointmentData.initialStartAt,
          });
        } else {
          // id: null is not allowed by the schema for creating appointments
          input.id = undefined;

          return createAppointment({
            input,
            sendConfirmationEmail: this.sendConfirmationEmail,
          });
        }
      };

      saveAppointment()
        .then((appointment) => {
          if (this.$route.name === 'customer-edit-appointment') {
            this.$router.push({
              name: 'customer-appointment',
              params: { appointmentId: appointment.id },
            });
          } else {
            this.$router.push({
              name: 'appointment',
              params: { appointmentId: appointment.id },
            });
          }
        })
        .catch(() => {
          // The promise will reject when the backend returns an error response for the create or update mutation
          // This might happen when the availabilities or requirements have changed while the user has the form open
          // So here we refetch the requirements and load new allocations, which will also trigger the availabilties query because of the watcher

          this.reloadAllAllocations();
        })
        .finally(() => {
          this.isSaving = false;
          if (this.currentOnboardingFlow === 'appointment') {
            eventBus.$emit('open-task-menu');
            eventBus.$emit('update-task', 'create_appointment');
            this.currentOnboardingFlow = '';
          }
        });
    },
    toggleRepeat(value: boolean) {
      // The watch on showRrule clears the rrule when toggled off.
      this.showRrule = value;
    },
    submitSeries() {
      // Check the whole set before creating, so the user can resolve bad slots first.
      this.isSaving = true;
      this.series.checkSeriesAvailabilities(0).then((results) => {
        this.isSaving = false;
        const anyUnavailable = results.some(
          (result) => result && !result.available,
        );
        if (anyUnavailable) {
          modal('confirmation', {
            message: this.$t(
              'appointment.create.series.unavailable_confirmation',
            ),
          }).then(() => {
            this.saveSeries();
          });
        } else {
          this.saveSeries();
        }
      });
    },
    saveSeries() {
      this.isSaving = true;

      const input = {
        locationId: this.formData.locationId as number,
        customerId: this.formData.customerId || undefined,
        notes: this.formData.notes || undefined,
        entries: this.series.entries.map((entry) => ({
          startAt: entry.startAt,
          partsAttributes: entry.partsAttributes,
        })),
      };

      createAppointments({
        input,
        sendConfirmationEmail: this.sendConfirmationEmail,
      })
        .then((appointments) => {
          const appointment = appointments[0];
          if (appointment) {
            this.$router.push({
              name: 'appointment',
              params: { appointmentId: appointment.id },
            });
          }
        })
        .catch((error: CreateAppointmentsError) => {
          this.handleSeriesError(error);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    handleSeriesError(error: CreateAppointmentsError) {
      if (error?.entryErrors) {
        // Map the per-entry errors (keyed by index) back onto the slots so the
        // user can see which date(s) to change.
        const errorsByDateId: Record<string, string[]> = {};
        this.series.seriesDates.forEach((date, index) => {
          const entryError = error.entryErrors?.[String(index)];
          if (entryError) {
            errorsByDateId[date.id] = entryError.missing_resource
              ? [this.$t('appointment.create.series.missing_resource')]
              : entryError.messages?.length
                ? entryError.messages
                : [this.$t('appointment.create.series.unavailable')];
          }
        });
        this.series.setSubmitErrors(errorsByDateId);
        // Refresh the availability badges to reflect the current state.
        this.series.checkSeriesAvailabilities(0);
      } else if (error?.batchError) {
        modal('warning', {
          message: this.$t('appointment.create.series.batch_error', {
            error: error.batchError,
          }),
        });
      }
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
  height: 100%;
}

.main {
  .base:not(.smallScreen) & {
    display: flex;
    height: 100%;
  }

  .base:not(.smallScreen):not(.previewMode) & {
    align-items: stretch;
  }

  .base.smallScreen & {
    padding: $spacing * 0.5;
  }

  .base.previewMode & {
    flex-direction: column;
  }
}

.content {
  .base:not(.smallScreen):not(.previewMode) & {
    width: 72.5%;
    padding: $spacing * 1.5;
  }

  .base:not(.smallScreen) & {
    overflow-y: auto;
  }

  .base.smallScreen & {
    padding: 0;
  }

  .base.previewMode & {
    height: 100%;
    padding: $spacing;
  }
}

.sidebar {
  .base:not(.smallScreen):not(.previewMode) & {
    width: 27.5%;
    border-left: 1px solid var(--color-border);
    background-color: var(--color-bg);
  }

  .base.previewMode & {
    border-top: 1px solid var(--color-border);
    background-color: var(--color-bg);
    flex-shrink: 0;
    border-radius: 0 0 $modal-radius $modal-radius;
  }
}
.disabled {
  & > * {
    pointer-events: none;
    opacity: 0.5;
  }
}
.section {
  &:not(:last-child) {
    margin-bottom: $spacing * 1.5;
  }
}
.dateTimeRow {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: $spacing * 0.5;
}
.cardContent {
  margin-top: $spacing;
}

.rule {
  height: 1px;
  background-color: var(--color-border);
  margin: $spacing * 1.5 0 $spacing;
}
</style>
